import { Component, OnInit, computed, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import {
  ApiResult,
  ApiPaginationResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import {
  DisplayArea,
  DisplayAreaItem,
  UpdateDisplayArea,
} from '../../../models/display-area.model';
import { DisplayAreaService } from '../../../services/display-area.service';
import { BrandService } from '../../../services/brand.service';
import { CategoryService } from '../../../services/category.service';
import { ProductService } from '../../../services/product.service';
import { ModelService } from '../../../services/model.service';
import { ModelVersionService } from '../../../services/model-version.service';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { ProductStatus } from '../../../models/product.model';

interface SourceItem {
  id: string;
  name: string;
  imageUrl?: string;
  description?: string;
}

@Component({
  selector: 'app-display-area-update',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    CheckboxModule,
    ProgressSpinnerModule,
    ToolbarModule,
    ToastModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
  ],
  templateUrl: './display-area-update.component.html',
})
export class DisplayAreaUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private formGroupService = inject(FormGroupService);
  private displayAreaService = inject(DisplayAreaService);
  private brandService = inject(BrandService);
  private categoryService = inject(CategoryService);
  private productService = inject(ProductService);
  private modelService = inject(ModelService);
  private modelVersionService = inject(ModelVersionService);

  formGroup: FormGroup = this.fb.group({
    name: [null, [Validators.required, Validators.maxLength(255)]],
    description: [null, [Validators.maxLength(255)]],
    itemType: [{ value: null, disabled: true }],
    items: [[] as DisplayAreaItem[]],
  });

  loading = signal(false);
  itemsLoading = signal(false);

  displayAreaId = signal<string | null>(null);
  maxItems = signal<number>(0);

  availableItems = signal<SourceItem[]>([]);
  itemType = signal<string>('Unknown');

  displayAreaTypeOptions = [
    { label: 'Produto', value: 'Product' },
    { label: 'Modelo', value: 'Model' },
    { label: 'Versão de Modelo', value: 'ModelVersion' },
    { label: 'Categoria', value: 'Category' },
    { label: 'Marca', value: 'Brand' },
  ];

  get selectedCount(): number {
    const items =
      (this.formGroup.get('items')?.value as DisplayAreaItem[]) ?? [];
    return items.length;
  }

  get hasReachedMaxItems(): boolean {
    const max = this.maxItems();
    return max <= 0 ? this.selectedCount >= max : this.selectedCount >= max;
  }

  get hasItemSku(): boolean {
    return this.formGroup.get('itemType')!.value == 'Product';
  }

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.displayAreaId.set(id);
      this.loadDisplayArea(id);
    }
  }

  isSelected(sourceId: string): boolean {
    const selectedItems =
      (this.formGroup.get('items')?.value as DisplayAreaItem[]) ?? [];
    return !!selectedItems.find((x) => x.referenceId === sourceId);
  }

  onToggleItem(source: SourceItem, checked: boolean): void {
    const control = this.formGroup.get('items');
    const current = (
      (control?.value as DisplayAreaItem[]) ?? []
    ).slice() as DisplayAreaItem[];

    if (checked) {
      const already = current.find((x) => x.referenceId === source.id);
      if (already) {
        return;
      }

      const nextLength = current.length + 1;
      const max = this.maxItems();

      if (max > 0 && nextLength > max) return;

      const newItem: DisplayAreaItem = {
        referenceId: source.id,
        displayOrder: current.length,
      };

      current.push(newItem);
    } else {
      const filtered = current.filter((x) => x.referenceId !== source.id);
      filtered.forEach((x, index) => (x.displayOrder = index));
      control?.setValue(filtered);
      control?.markAsDirty();
      return;
    }

    control?.setValue(current);
    control?.markAsDirty();
  }

  getDisplayOrder(sourceId: string): number | null {
    const items =
      (this.formGroup.get('items')?.value as DisplayAreaItem[]) ?? [];

    const found = items.find((x) => x.referenceId === sourceId);
    return found?.displayOrder ?? null;
  }

  onDisplayOrderChange(sourceId: string, value: number | null): void {
    const control = this.formGroup.get('items');
    if (!control) return;

    const items = (
      (control.value as DisplayAreaItem[]) ?? []
    ).slice() as DisplayAreaItem[];

    const target = items.find((x) => x.referenceId === sourceId);
    if (!target) {
      return;
    }

    const normalized = Number.isFinite(value as number) ? Number(value) : 0;
    target.displayOrder = normalized;

    control.setValue(items);
    control.markAsDirty();
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const id = this.displayAreaId();
    if (!id) return;

    const raw = this.formGroup.getRawValue();
    const body: UpdateDisplayArea = {
      name: raw.name,
      description: raw.description,
      items: raw.items as DisplayAreaItem[],
    };

    this.loading.set(true);

    let request$: Observable<ApiResult>;
    request$ = this.displayAreaService.update(id, body);

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Área de exibição atualizada com sucesso',
          life: 2000,
        });

        this.router.navigate(['/display-areas']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors?.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length === 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar a área de exibição',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private loadDisplayArea(id: string): void {
    this.loading.set(true);

    this.displayAreaService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const model = response.data as DisplayArea;

        this.maxItems.set(model.maxItems);
        this.itemType.set(model.itemType.toString());

        this.formGroup.patchValue({
          name: model.name,
          description: model.description,
          itemType: model.itemType,
          items: model.items ?? [],
        });

        this.loadItemsByType(model.itemType.toString());
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar a área de exibição',
            life: 3000,
          });
        }
      },
    });
  }

  private loadItemsByType(type: string): void {
    this.itemsLoading.set(true);

    let request$: Observable<ApiPaginationResult>;

    switch (type) {
      case 'Brand':
        request$ = this.brandService.getAll(
          0,
          1000,
          undefined,
          undefined,
          true
        );
        break;
      case 'Category':
        request$ = this.categoryService.getAll(
          0,
          1000,
          undefined,
          undefined,
          true
        );
        break;
      case 'Product':
        request$ = this.productService.getAll(
          0,
          1000,
          undefined,
          undefined,
          ProductStatus.Active
        );
        break;
      case 'Model':
        request$ = this.modelService.getAll(
          0,
          1000,
          undefined,
          undefined,
          true
        );
        break;
      case 'ModelVersion':
        request$ = this.modelVersionService.getAll(
          0,
          1000,
          undefined,
          undefined,
          true
        );
        break;
      default:
        this.itemsLoading.set(false);
        this.availableItems.set([]);
        return;
    }

    request$.subscribe({
      next: (response: ApiPaginationResult) => {
        this.itemsLoading.set(false);

        const page = response.data as PaginationResult;

        const mapped: SourceItem[] = page.items.map((x) => ({
          id: x.id,
          imageUrl: type == 'Product' ? x?.mainImageUrl : x?.imageUrl,
          name:
            type == 'ModelVersion' ? `${x.modelName} ${x.name}` : x.name ?? '',
          description: x?.sku ?? x.description,
        }));

        this.availableItems.set(mapped);

        const control = this.formGroup.get('items');
        if (control) {
          const current = (
            (control.value as DisplayAreaItem[]) ?? []
          ).slice() as DisplayAreaItem[];

          const validIds = new Set(mapped.map((m) => m.id));
          const filtered = current.filter((i) => validIds.has(i.referenceId));
          filtered.forEach((x, index) => (x.displayOrder = index));
          control.setValue(filtered);
        }
      },
      error: () => {
        this.itemsLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar itens para esta área',
          life: 3000,
        });
      },
    });
  }
}
